<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import Button from "@/components/Button.vue";
import Input from "@/components/Input.vue";
import Modal from "@/components/Modal.vue";
import DataTable from "@/components/DataTable.vue";
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/vue/24/outline";

import services from "@/services";
import { useToaster } from "ram-components";
import UserActivityDetailsModal from "@/components/UserActivityDetailsModal.vue";
import { label } from "@formkit/inputs";
import { useUserDataStore } from "@/stores/userDataStore";
import ImageViewer from "@/components/ImageViewer.vue";

type TabType =
  | "expected"
  | "in_hub"
  | "collected"
  | "user_activity"
  | "historical";

const activeTab = ref<TabType>("expected");
const searchQuery = ref("");
const dateFrom = ref("");
const dateTo = ref("");
const selectedUser = ref("all");
const isLoading = ref(false);
const closureReasons = ref<any[]>([]);
const userList = ref<any[]>([]);

const expectedParcels = ref<any[]>([]);
const inHubParcels = ref<any[]>([]);
const collectedParcels = ref<any[]>([]);
const userActivities = ref<any[]>([]);
const historicalRecords = ref<any[]>([]);

const showCloseOffModal = ref(false);
const selectedConsignmentNo = ref("");
const selectedClosureReason = ref("");

const showActivityModal = ref(false);
const activityModalType = ref<"scanned" | "handed_over" | "closed" | null>(
  null,
);
const activityUser = ref({ id: 0, name: "" });

const showImageModal = ref(false);
const previewImageUrl = ref("");
const isImageLoading = ref(false);
const modalTitle = ref("Image Preview");

const toaster = useToaster();
const userDataStore = useUserDataStore();

const expectedColumns = [
  { key: "consignmentID", label: "Consignment No" },
  { key: "recordDT", label: "Consigned DT" },
  { key: "receiverName", label: "Receiver Name" },
  { key: "receiverContactNumber", label: "Receiver Telephone" },
  { key: "shipperReference", label: "Shipper Reference" },
  { key: "actions", label: "Actions" },
];

const inHubColumns = [
  { key: "consignmentNo", label: "Consignment No" },
  { key: "consignedDT", label: "Consigned DT" },
  { key: "receiverName", label: "Receiver Name" },
  { key: "receiverTelephone", label: "Receiver Telephone" },
  { key: "shipperReference", label: "Shipper Reference" },
  { key: "age", label: "Age (Days)" },
  { key: "receivedBy", label: "Received By" },
];

const collectedColumns = [
  { key: "consignmentID", label: "Consignment No" },
  { key: "collectedDT", label: "Collected DT" },
  { key: "collectedTime", label: "Collected Time(24hrs)" },
  { key: "receiverName", label: "Receiver Name" },
  { key: "receiverContactNumber", label: "Receiver Telephone" },
  { key: "shipperReference", label: "Shipper Reference" },
  { key: "collectedBy", label: "Collected By" },
  { key: "handOverBy", label: "Handed Over By" },
  { key: "timeDays", label: "Time (Days)" },
  { key: "actions", label: "Actions" },
];

const userActivityColumns = [
  { key: "userName", label: "User Name" },
  { key: "parcelScannedCount", label: "Parcels Scanned" },
  { key: "parcelHandedOverCount", label: "Parcels Handed Over" },
  { key: "consignmentsClosedCount", label: "Consignments Closed" },
  { key: "loginTime", label: " Last Login" },
  { key: "logoutTime", label: "Last Logout" },
];

const historicalColumns = [
  { key: "consignmentID", label: "Consignment ID" },
  { key: "receiverName", label: "Receiver Name" },
  { key: "receiverTelephone", label: "Receiver Telephone" },
  { key: "status", label: "Status" },
  { key: "collectedBy", label: "Collected By" },
  { key: "collectedDT", label: "Collected DT" },
  { key: "collectedTime", label: "Collected Time(24hrs)" },
  { key: "scannedInBy", label: "Scanned In By" },
  { key: "scannedInDT", label: "Scanned In DT" },
  { key: "scannedInTime", label: "Scanned In Time(24hrs)" },
  { key: "closedDT", label: "Closed DT" },
  { key: "closedTime", label: "Closed Time(24hrs)" },
  { key: "closedBy", label: "Closed By" },
  { key: "reasonClosureName", label: "Close Off Reason" },
  {key:"actions", label:"Actions"}
];

const tabs = [
  { id: "expected" as TabType, label: "View Expected Parcels" },
  { id: "in_hub" as TabType, label: "View In-hub Parcels" },
  { id: "collected" as TabType, label: "View Collected Today" },
  { id: "user_activity" as TabType, label: "User Activity" },
  { id: "historical" as TabType, label: "Historical Search" },
];

const loadUserList = async () => {
  try {
    const response = await services.getUsersLookup();
    userList.value = response.data.result || [];
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load user list!",
    });
  }
};

const loadClosureReasons = async () => {
  try {
    const response = await services.getClosureReasonList();

    closureReasons.value = response.data.result || [];
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load closure reasons",
    });
  }
};
const loadExpectedParcels = async () => {
  isLoading.value = true;
  try {
    const searchId = searchQuery.value.trim();
    const response = await services.getViewExpectedParcels(searchId);
    expectedParcels.value = response.data.result.map((parcel: any) => ({
      ...parcel,
      recordDT: parcel.recordDT ? parcel.recordDT.split("T")[0] : "",
    }));
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load expected parcels",
    });
  } finally {
    isLoading.value = false;
  }
};

const loadInHubParcels = async () => {
  isLoading.value = true;
  try {
    const response = await services.getViewInHubParcels();
    inHubParcels.value = response.data.result.map((parcel: any) => ({
      ...parcel,
      consignedDT: parcel.consignedDT ? parcel.consignedDT.split("T")[0] : "",
    }));
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load in-hub parcels",
    });
  } finally {
    isLoading.value = false;
  }
};

const loadCollectedParcels = async () => {
  isLoading.value = true;
  try {
    const response = await services.getViewCollected();
    collectedParcels.value = response.data.result.map((parcel: any) => ({
      ...parcel,
      collectedDT: parcel.collectedDT ? parcel.collectedDT.split("T")[0] : "",
    }));
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to load collected parcels",
    });
  } finally {
    isLoading.value = false;
  }
};

const applyUserActivityFilter = async () => {
  isLoading.value = true;
  try {
    const payload = {
      fromDate: dateFrom.value,
      toDate: dateTo.value,
      userID: selectedUser.value === "all" ? 0 : selectedUser.value,
    };
    const response = await services.getViewUserActivity(payload);
    userActivities.value = response.data.result.map((parcel: any) => ({
      ...parcel,
      loginTime: parcel.loginTime
        ? parcel.loginTime.slice(0, 16).replace("T", " , ")
        : "",
      logoutTime: parcel.logoutTime
        ? parcel.logoutTime.slice(0, 16).replace("T", " , ")
        : "",
    }));
  } catch (error: any) {
    const validationErrors = error.response?.data?.errors;

    if (
      (validationErrors && validationErrors["$.fromDate"]) ||
      validationErrors["$.toDate"]
    ) {
      toaster.push({
        type: "error",
        text: "Please select a date range",
      });
    } else {
      toaster.push({
        type: "error",
        text: error.response?.data?.title || "Failed to load User Activity",
      });
    }
  } finally {
    isLoading.value = false;
  }
};

const applyHistoricalSearch = async () => {
  isLoading.value = true;
  try {
    const payload = {
      fromDate: dateFrom.value,
      toDate: dateTo.value,
    };
    const response = await services.getViewHistoricalSearch(payload);
    historicalRecords.value = response.data.result.map((parcel: any) => ({
      ...parcel,
      collectedDT: parcel.collectedDT ? parcel.collectedDT.split(" ")[0] : "",
      scannedInDT: parcel.scannedInDT ? parcel.scannedInDT.split(" ")[0] : "",
      closedDT: parcel.closedDT ? parcel.closedDT.split(" ")[0] : "",
    }));
  } catch (error: any) {
    const validationErrors = error.response?.data?.errors;

    if (
      (validationErrors && validationErrors["$.fromDate"]) ||
      validationErrors["$.toDate"]
    ) {
      toaster.push({
        type: "error",
        text: "Please select a date range",
      });
    } else {
      toaster.push({
        type: "error",
        text: error.response?.data?.title || "Failed to records",
      });
    }
  } finally {
    isLoading.value = false;
  }
};

const handleCloseOff = async () => {
  if (!selectedClosureReason.value) return;

  isLoading.value = true;

  try {
    const userId = Number(localStorage.getItem("activeUserID")) || 0;

    const payload = {
      consignmentID: selectedConsignmentNo.value,
      reasonClosureID: Number(selectedClosureReason.value),
      createdBy: userId,
    };

    const response = await services.saveConfirmCloseOff(payload);

    if (
      response.data &&
      (response.data.statusCode === 200 || response.data.status === "SUCCEEDED")
    ) {
      closeCloseOffModal();
      toaster.push({
        type: "success",
        text: "Consignment Closed off Successfully!",
      });

      await loadExpectedParcels();
    } else {
      toaster.push({
        type: "error",
        text: "Failed to close off Consignment!",
      });
    }
  } catch (error) {
    toaster.push({
      type: "error",
      text: "Failed to close off consignment!",
    });
  } finally {
    isLoading.value = false;
  }
};

watch(searchQuery, (newVal) => {
  if (activeTab.value === "expected") {
    loadExpectedParcels();
  }
});

const currentData = computed(() => {
  switch (activeTab.value) {
    case "expected":
      return expectedParcels.value;
    case "in_hub":
      return inHubParcels.value;
    case "collected":
      return collectedParcels.value;
    case "user_activity":
      return userActivities.value;
    case "historical":
      if (!searchQuery.value) return historicalRecords.value;

      const lowerQuery = searchQuery.value.toLowerCase();

      return historicalRecords.value.filter((item: any) => {
        return (
          (item.consignmentID?.toLowerCase() || "").includes(lowerQuery) ||
          (item.receiverName?.toLowerCase() || "").includes(lowerQuery) ||
          (item.receiverTelephone?.toLowerCase() || "").includes(lowerQuery) ||
          (item.status?.toLowerCase() || "").includes(lowerQuery) ||
          (item.collectedBy?.toLowerCase() || "").includes(lowerQuery) ||
          (item.collectedDT?.toLowerCase() || "").includes(lowerQuery) ||
          (item.collectedTime?.toLowerCase() || "").includes(lowerQuery) ||
          (item.scannedInBy?.toLowerCase() || "").includes(lowerQuery) ||
          (item.scannedInDT?.toLowerCase() || "").includes(lowerQuery) ||
          (item.scannedInTime?.toLowerCase() || "").includes(lowerQuery) ||
          (item.closedBy?.toLowerCase() || "").includes(lowerQuery) ||
          (item.closedDT?.toLowerCase() || "").includes(lowerQuery) ||
          (item.closedTime?.toLowerCase() || "").includes(lowerQuery) ||
          (item.reasonClosureName?.toLowerCase() || "").includes(lowerQuery)
        );
      });
    default:
      return [];
  }
});

const currentColumns = computed(() => {
  switch (activeTab.value) {
    case "expected":
      return expectedColumns;
    case "in_hub":
      return inHubColumns;
    case "collected":
      return collectedColumns;
    case "user_activity":
      return userActivityColumns;
    case "historical":
      return historicalColumns;
    default:
      return [];
  }
});

const changeTab = async (tab: TabType) => {
  activeTab.value = tab;
  dateFrom.value = "";
  dateTo.value = "";
  selectedUser.value = "all";
  searchQuery.value = "";
  if (tab === "expected") await loadExpectedParcels();
  else if (tab === "in_hub") await loadInHubParcels();
  else if (tab === "collected") await loadCollectedParcels();
  else if (tab === "user_activity") userActivities.value = [];
  else if (tab === "historical") historicalRecords.value = [];
};

const openCloseOffModal = (consignmentNo: string) => {
  selectedConsignmentNo.value = consignmentNo;
  showCloseOffModal.value = true;
};

const closeCloseOffModal = () => {
  showCloseOffModal.value = false;
  selectedConsignmentNo.value = "";
  selectedClosureReason.value = "";
};

const handleViewDetails = (
  row: any,
  type: "scanned" | "handed_over" | "closed",
) => {
  const count =
    type === "scanned"
      ? row.parcelScannedCount
      : type === "handed_over"
        ? row.parcelHandedOverCount
        : row.consignmentsClosedCount;

  if (!count || count === 0) return;

  activityModalType.value = type;
  activityUser.value = {
    id: row.userID || row.recordID,
    name: row.userName,
  };

  showActivityModal.value = true;
};

const closeActivityModal = () => {
  showActivityModal.value = false;
  activityModalType.value = null;
};

onMounted(async () => {
  loadExpectedParcels(), loadClosureReasons(), loadUserList();
});

const handleImageSelect = async (node: any, row: any) => {
  const selection = node.value;
  if (!selection) return;

  previewImageUrl.value = "";
  modalTitle.value =
    selection === "id" ? "Collector ID" : "Collector Signature";
  isImageLoading.value = true;
  showImageModal.value = true;

  node.input("");

  try {
    const payload = {
      consignmentID: row.consignmentID || row.consignmentNo,
      imageType: selection === "id" ? 1 : 2,
    };

    const response = await services.getParcelImageUrl(payload);

    if (response.data && response.data.result) {
      previewImageUrl.value = response.data.result[0].imageUrl;
    } else {
      toaster.push({ type: "error", text: "Image URL missing in response" });
      showImageModal.value = false;
    }
  } catch (error) {
    toaster.push({ type: "error", text: "Error fetching image" });
    showImageModal.value = false;
  } finally {
    isImageLoading.value = false;
  }
};
</script>
<template>
  <div>
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Reports</h1>
      <p class="mt-1 text-gray-500">
        Generate and view reports on parcel activity.
      </p>
    </div>

    <div class="rounded-xl border border-gray-200 bg-white">
      <div class="border-b border-gray-200">
        <div class="flex flex-wrap gap-2 p-4">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="changeTab(tab.id)"
            :class="[
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-ram-red text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            ]"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div
        v-if="activeTab === 'user_activity'"
        class="border-b border-gray-200 bg-gray-50 p-4"
      >
        <div class="grid items-end gap-4 md:grid-cols-5">
          <Input
            v-model="dateFrom"
            type="date"
            label="Date From"
            placeholder="yyyy/mm/dd"
          />
          <Input
            v-model="dateTo"
            type="date"
            label="Date To"
            label-class="mb-1"
            placeholder="yyyy/mm/dd"
          />
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700"
              >User</label
            >
            <select
              v-model="selectedUser"
              class="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sbsa-blue"
            >
              <option value="all">All Users</option>
              <option
                v-for="user in userList"
                :key="user.recordID"
                :value="user.recordID"
              >
                {{ user.userName }}
              </option>
            </select>
          </div>
          <Button
            @click="applyUserActivityFilter"
            :disabled="!selectedUser && !dateFrom && !dateTo"
            variant="ram"
            :loading="isLoading && activeTab === 'user_activity'"
            class="h-[42px] w-full"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      <div
        v-if="activeTab === 'historical'"
        class="border-b border-gray-200 bg-gray-50 p-4"
      >
        <div class="grid items-end gap-4 md:grid-cols-5">
          <Input
            v-model="dateFrom"
            type="date"
            label="Date From"
            placeholder="yyyy/mm/dd"
          />
          <Input
            v-model="dateTo"
            type="date"
            label="Date To"
            placeholder="yyyy/mm/dd"
          />

          <Button
            @click="applyHistoricalSearch"
            :loading="isLoading && activeTab === 'historical'"
            class="h-[42px] w-full"
            variant="ram"
          >
            Search
          </Button>
        </div>
        <div class="relative mt-2 md:col-span-2">
          <label class="mb-1 block text-sm font-medium text-gray-700"
            >Search Query</label
          >
          <MagnifyingGlassIcon
            class="absolute left-3 top-1/2 h-5 text-gray-400"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search"
            class="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-sbsa-blue"
          />
        </div>
      </div>

      <div
        v-if="activeTab === 'expected'"
        class="border-b border-gray-200 bg-gray-50 p-4"
      >
        <div class="flex justify-end">
          <div class="relative w-64">
            <MagnifyingGlassIcon
              class="absolute left-3 top-1/2 h-5 -translate-y-1/2 text-gray-400"
            />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by Consignment ID"
              class="w-full rounded-lg border border-gray-300 py-2.5 pl-10 focus:outline-none focus:ring-2 focus:ring-sbsa-blue"
            />
          </div>
        </div>
      </div>

      <div class="p-4">
        <div class="mb-4">
          <h3 class="font-semibold text-gray-900">
            {{ tabs.find((t) => t.id === activeTab)?.label }} ({{
              currentData.length
            }})
          </h3>
        </div>

        <DataTable
          :key="activeTab"
          :columns="currentColumns"
          :data="currentData"
          :loading="isLoading"
          empty-message="No data available"
        >
          <template #cell-actions="{ row }">
            <Button
              v-if="activeTab === 'expected'"
              size="sm"
              :loading="isLoading"
              variant="ram"
              class="text-nowrap"
              @click.stop="openCloseOffModal(row.consignmentID)"
            >
              Close Off
            </Button>
            <div v-if="activeTab === 'collected' ||(activeTab === 'historical' && row.status?.toLowerCase() === 'collected') " class="w-32">
              <FormKit
                type="select"
                placeholder="View.."
                :options="[
                  { label: 'Collector ID', value: 'id' },
                  { label: 'Signature', value: 'sig' },
                ]"
                @input="(val, node) => handleImageSelect(node, row)"
                :classes="{
                  outer: 'm-0',
                  inner: `flex items-center bg-white border border-gray-300 rounded-lg 
                          px-3 py-1.5 shadow-sm transition-all duration-200
                          hover:border-ram-red hover:bg-gray-50 cursor-pointer
                          focus-within:ring-2 focus-within:ring-ram-red/20 focus-within:border-ram-red`,
                  input:
                    'text-sm font-medium text-gray-700 bg-transparent border-none focus:ring-0 cursor-pointer w-full',
                  placeholder: 'text-gray-500 font-normal',
                }"
              />
            </div>
          </template>

          <template #cell-parcelScannedCount="{ row, value }">
            <span
              @click="handleViewDetails(row, 'scanned')"
              :class="
                value > 0
                  ? 'cursor-pointer font-semibold text-blue-600 hover:underline'
                  : 'text-gray-500'
              "
            >
              {{ value }}
            </span>
          </template>

          <template #cell-parcelHandedOverCount="{ row, value }">
            <span
              @click="handleViewDetails(row, 'handed_over')"
              :class="
                value > 0
                  ? 'cursor-pointer font-semibold text-blue-600 hover:underline'
                  : 'text-gray-500'
              "
            >
              {{ value }}
            </span>
          </template>

          <template #cell-consignmentsClosedCount="{ row, value }">
            <span
              @click="handleViewDetails(row, 'closed')"
              :class="
                value > 0
                  ? 'cursor-pointer font-semibold text-blue-600 hover:underline'
                  : 'text-gray-500'
              "
            >
              {{ value }}
            </span>
          </template>
        </DataTable>
      </div>
    </div>

    <Modal
      :is-open="showCloseOffModal"
      title="Close Off Consignment"
      @close="closeCloseOffModal"
    >
      <div class="space-y-4">
        <div class="flex items-center gap-3 rounded-lg p-3">
          <div class="rounded-full bg-red-200 px-2 py-2">
            <ExclamationTriangleIcon class="h-6 w-6 text-red-700" />
          </div>
          <div>
            <p class="text-sm text-black">
              Consignment ID: <strong>{{ selectedConsignmentNo }}</strong>
            </p>
          </div>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">
            Reason for Closure*
          </label>
          <select
            v-model="selectedClosureReason"
            class="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sbsa-blue"
          >
            <option value="">Select a reason...</option>
            <option
              v-for="reason in closureReasons"
              :key="reason.recordID"
              :value="reason.recordID"
            >
              {{ reason.reasonClosureName }}
            </option>
          </select>
        </div>
      </div>

      <template #footer>
        <Button variant="secondary" @click="closeCloseOffModal">
          Cancel
        </Button>
        <Button
          :loading="isLoading"
          variant="ram"
          @click="handleCloseOff"
          :disabled="!selectedClosureReason"
        >
          Confirm Close Off
        </Button>
      </template>
    </Modal>
  </div>
  <UserActivityDetailsModal
    :is-open="showActivityModal"
    :user-id="activityUser.id"
    :user-name="activityUser.name"
    :date-from="dateFrom"
    :date-to="dateTo"
    :type="activityModalType"
    @close="closeActivityModal"
  />
  <ImageViewer
    :is-open="showImageModal"
    :image-url="previewImageUrl"
    :is-loading="isImageLoading"
    :title="modalTitle"
    @close="showImageModal = false"
  />
</template>
